import React from "react";
import { SingleValue } from "react-select";

import {
  ElementFieldMappingValidationError,
  KintoneExportFieldCorrespondences,
  kintoneFieldReadableTypeName,
  KintoneFormField,
  KintoneFormFields,
  ProcedureType,
} from "@graffer-inc/cloud-gov-core";
import {
  ReactSelect,
  ReactSelectOption,
  TextButton,
  WarningDialog,
} from "@graffer-inc/console-design-kit";
import { InputElementUnion, typeLabel } from "@graffer-inc/form-schema";
import { UpdateKintoneExportSettingInput } from "@graffer-inc/smart-apply";

import {
  fieldMappingErrorCodeToMessage,
  validateElementFieldMappings,
} from "../../../../../../../application/domain/kintone-export";
import {
  elementToReadableString,
  useElementSelect,
  useFormSchemaElementCandidates,
  useFreshIds,
  useKintoneFieldCandidates,
} from "./hook";
import { ElementFieldMappingsJsonEditor } from "./json-editor";
import { KintoneFieldSelect } from "./kintone-field-select";

type ElementFieldMappingsFormProps = {
  elementFieldMappings: UpdateKintoneExportSettingInput["elementFieldMappings"];
  onChange: (
    elementFieldMappings: UpdateKintoneExportSettingInput["elementFieldMappings"],
  ) => void;
  procedure: ProcedureType;
  kintoneFormFields?: KintoneFormFields;

  usedKintoneFields?: Set<KintoneFormField["code"]>;

  showJsonEditor?: boolean;
};

export const ElementFieldMappingsForm: React.FC<
  ElementFieldMappingsFormProps
> = ({
  elementFieldMappings,
  onChange,
  procedure,
  kintoneFormFields,
  usedKintoneFields,
  showJsonEditor,
}) => {
  const formSchema = React.useMemo(() => {
    return (
      procedure.published?.template.formSchema ??
      procedure.draft.template.formSchema
    );
  }, [procedure]);

  const { errors } = React.useMemo(() => {
    return validateElementFieldMappings({
      value: elementFieldMappings.map((mapping, i) => {
        return {
          ...mapping,
          mappingKey: i,
        };
      }),
      formSchema,
      kintoneFields: kintoneFormFields,
      usedKintoneFieldCode: usedKintoneFields,
    });
  }, [elementFieldMappings, formSchema, kintoneFormFields, usedKintoneFields]);

  const { elementsMap, elements } = useFormSchemaElementCandidates({
    formSchema,
  });
  const { filteredKintoneFields } = useKintoneFieldCandidates({
    kintoneFormFields,
  });
  const mappingIds = useFreshIds(elementFieldMappings, "element-mappings");

  return (
    <div className="columns">
      <div className="column">
        <div className="field">
          <label className="label">入力項目のマッピング設定</label>
          <p>{`現在、${elementFieldMappings.length}個のマッピングが設定されています。`}</p>
          <ListAllElementMappingsButton
            onProceed={() => {
              onChange(
                elements.map((element) => {
                  return {
                    formElementId: element.id,
                    kintoneFieldCode: "",
                  };
                }),
              );
            }}
          />
          {showJsonEditor && (
            <ElementFieldMappingsJsonEditor
              value={elementFieldMappings}
              onChange={onChange}
            />
          )}
          {elementFieldMappings.map((mapping, i) => {
            const error = errors.find(({ mappingKey }) => mappingKey === i);

            return (
              <div key={mappingIds[i]}>
                <div
                  style={{ position: "relative" }}
                  onClick={() => {
                    const newElementFieldMappings = [...elementFieldMappings];
                    newElementFieldMappings.splice(i, 1);
                    onChange(newElementFieldMappings);
                  }}
                >
                  <span className="icon pl-5" style={{ position: "absolute" }}>
                    <i className="fas fa-window-close"></i>
                  </span>
                </div>

                <div
                  className="my-2 ml-2 pl-5"
                  style={{ borderLeft: "0.25rem solid silver" }}
                >
                  <ElementFieldMappingForm
                    applicationRecordMapping={mapping}
                    onChange={(update) => {
                      const newElementFieldMappings = [...elementFieldMappings];
                      newElementFieldMappings[i] = update;
                      onChange(newElementFieldMappings);
                    }}
                    elementsMap={elementsMap}
                    kintoneFieldsMap={filteredKintoneFields}
                  />
                  {error && (
                    <MappingErrorMessage
                      error={error}
                      element={elementsMap[mapping.formElementId]}
                      kintoneField={
                        filteredKintoneFields
                          ? filteredKintoneFields[mapping.kintoneFieldCode]
                          : undefined
                      }
                    />
                  )}
                </div>
              </div>
            );
          })}
          <div className="ml-2">
            <button
              className="button is-fullwidth"
              onClick={() => {
                onChange([
                  ...elementFieldMappings,
                  {
                    kintoneFieldCode: "",
                    formElementId: "",
                  },
                ]);
              }}
            >
              <span className="icon has-text-dark">
                <i className="fas fa-plus"></i>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

type MappingErrorMessageProps = {
  error: ElementFieldMappingValidationError;
  element?: InputElementUnion;
  kintoneField?: KintoneFormField;
};

const MappingErrorMessage: React.FC<MappingErrorMessageProps> = ({
  error,
  element,
  kintoneField,
}) => {
  if (
    error.errorCode === "OPTION_MISMATCH" &&
    !!element &&
    "enum" in element &&
    !!kintoneField &&
    "options" in kintoneField
  ) {
    return (
      <div className="help is-danger">
        <p>{fieldMappingErrorCodeToMessage(error.errorCode)}</p>
        <p>
          {`入力項目の選択肢：${element.enum
            .map((option) => option.text)
            .join(", ")}`}
        </p>
        <p>
          {`kintone フィールドの選択肢：${Object.keys(
            kintoneField.options,
          ).join(", ")}`}
        </p>
      </div>
    );
  }

  if (
    !!element &&
    (error.errorCode === "KINTONE_FIELD_CODE_EMPTY" ||
      error.errorCode === "TYPE_MISMATCH")
  ) {
    return (
      <p className="help is-danger">
        {fieldMappingErrorCodeToMessage(error.errorCode)}
        <br />
        {`入力項目が${typeLabel(
          element.inputType,
        )}のとき、 kintone フィールドには${KintoneExportFieldCorrespondences[
          element.inputType
        ]
          .map(kintoneFieldReadableTypeName)
          .join(", ")}を指定してください。`}
      </p>
    );
  }

  return (
    <p className="help is-danger">
      {fieldMappingErrorCodeToMessage(error.errorCode)}
    </p>
  );
};

type ElementFieldMappingFormProps = {
  applicationRecordMapping: UpdateKintoneExportSettingInput["elementFieldMappings"][number];
  onChange: (
    applicationRecordMapping: UpdateKintoneExportSettingInput["elementFieldMappings"][number],
  ) => void;
  elementsMap: Record<InputElementUnion["id"], InputElementUnion>;
  kintoneFieldsMap?: KintoneFormFields;
};

const ElementFieldMappingForm: React.FC<ElementFieldMappingFormProps> = ({
  applicationRecordMapping,
  onChange,
  elementsMap,
  kintoneFieldsMap,
}) => {
  return (
    <div>
      <FormElementSelect
        value={applicationRecordMapping.formElementId}
        onChange={(formElementId) => {
          onChange({
            ...applicationRecordMapping,
            formElementId,
          });
        }}
        elementsMap={elementsMap}
      />

      {kintoneFieldsMap ? (
        <KintoneFieldSelect
          value={applicationRecordMapping.kintoneFieldCode}
          onChange={(kintoneFieldCode) => {
            onChange({
              ...applicationRecordMapping,
              kintoneFieldCode,
            });
          }}
          kintoneFieldsMap={kintoneFieldsMap}
          label="kintone フィールド"
        />
      ) : (
        <div className="mb-2">
          <label>kintone フィールドコード</label>
          <div className="control">
            <input
              className="input"
              value={applicationRecordMapping.kintoneFieldCode}
              onChange={(e) => {
                onChange({
                  ...applicationRecordMapping,
                  kintoneFieldCode: e.target.value,
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const FormElementSelect: React.FC<{
  value: UpdateKintoneExportSettingInput["elementFieldMappings"][number]["formElementId"];
  onChange: (
    formElementId: UpdateKintoneExportSettingInput["elementFieldMappings"][number]["formElementId"],
  ) => void;
  elementsMap: Record<InputElementUnion["id"], InputElementUnion>;
}> = ({ value, onChange, elementsMap }) => {
  const [selected, setSelected] = React.useState<
    { value: string; label: string } | undefined
  >(() => {
    if (!value) {
      return undefined;
    }
    const element = elementsMap[value];
    if (!element) {
      // 基本的にありえない想定
      return {
        value,
        label: `存在しない項目 ${value} が指定されています`,
      };
    }
    return {
      value: element.id,
      label: elementToReadableString(element),
    };
  });

  const { options, onInputChange, isLoading } = useElementSelect({
    candidates: elementsMap,
  });

  const helpText = React.useMemo(() => {
    if (!value) {
      return undefined;
    }
    const element = elementsMap[value];
    if (!element) {
      return undefined;
    }
    if ("enum" in element) {
      return `選択肢: ${element.enum.map((option) => option.text).join(", ")}`;
    }
    return undefined;
  }, [value, elementsMap]);

  return (
    <div className="mb-2">
      <label>フォームの入力項目</label>
      <div className="control">
        <ReactSelect
          isClearable
          placeholder="項目名または id で検索"
          value={selected}
          onInputChange={onInputChange}
          isLoading={isLoading}
          loadingMessage={() => "項目を検索中..."}
          options={options}
          onChange={(v) => {
            const selected = v as SingleValue<ReactSelectOption<string>>;
            setSelected(selected ?? undefined);
            onChange(selected?.value ?? "");
          }}
          noOptionsMessage={({ inputValue }) =>
            inputValue === ""
              ? "項目名または id を入力してください"
              : "入力項目が見つかりません"
          }
          label=""
          description=""
        />
      </div>
      {!!helpText && <p className="help">{helpText}</p>}
    </div>
  );
};

const ListAllElementMappingsButton: React.FC<{
  onProceed: () => void;
}> = ({ onProceed }) => {
  const [showDialog, setShowDialog] = React.useState(false);
  return (
    <>
      <TextButton
        icon="ListSettingIcon"
        text="すべての入力項目のマッピング欄を配置する"
        onClick={() => {
          setShowDialog(true);
        }}
      />
      {showDialog && (
        <WarningDialog
          title="すべての入力項目のマッピング欄を配置する"
          contents="現在の「入力項目のマッピング設定」を削除して、フォームに設定されているすべての入力項目に対するマッピング欄を配置します。"
          proceedText="現在の設定を削除して配置"
          cancelText="キャンセル"
          onProceed={() => {
            setShowDialog(false);
            onProceed();
          }}
          onCancel={() => {
            setShowDialog(false);
          }}
        />
      )}
    </>
  );
};
